import { appConfig } from '../data/AppConfig';
import axios from 'axios';
import { useEffect, useState } from 'react';

/**
 * @returns The value of the cookie with the specified name, or `null` if the specified cookie doesn't exist.
 * @example If there's a cookie set that looks like this: MY_SESSION=abcd, then getCookie('MY_SESSION') will return "abcd"
 */
const getCookie = (name: string): string | null => {
    // Split cookie string and get all individual name=value pairs in an array
    let cookieArr = document.cookie.split(';');

    // Loop through the array elements
    for (let i = 0; i < cookieArr.length; i++) {
        let cookiePair = cookieArr[i].split('=');

        /* Removing whitespace at the beginning of the cookie name
        and compare it with the given string */
        if (name === cookiePair[0].trim()) {
            // Decode the cookie value and return
            return decodeURIComponent(cookiePair[1]);
        }
    }

    // Return null if not found
    return null;
};

/**
 * One shared in-flight request for the whole app.
 *
 * `useCsrf` is called from ~25 components, and each mount used to fire its own
 * `/sanctum/csrf-cookie`. Every one of those arrived without a session cookie,
 * so Laravel minted a fresh session and returned a new `Set-Cookie` — and the
 * last response to land won. That raced against the OAuth callback: the session
 * `Auth::login()` had just written to was replaced by an anonymous one, and the
 * following `/api/me` came back 401 even though the login had succeeded.
 */
let inFlight: Promise<string | null> | null = null;

const fetchCsrfCookie = (): Promise<string | null> => {
    if (inFlight) {
        return inFlight;
    }

    inFlight = axios
        .get(`${appConfig.REACT_APP_BACKEND_URL}/sanctum/csrf-cookie`, {
            // Required by Sanctum's SPA flow. Without it the browser neither
            // sends the existing session cookie nor stores the returned one, so
            // every call created an orphan session instead of reusing ours.
            withCredentials: true,
        })
        .then(() => getCookie('XSRF-TOKEN'))
        .catch(() => null)
        .finally(() => {
            inFlight = null;
        });

    return inFlight;
};

/**
 * Fetches a CSRF token from the backend and stores it in state, making it available for later use.
 */
const useCsrf = () => {
    const [csrfToken, setCsrfToken] = useState<string | null>(() => getCookie('XSRF-TOKEN'));
    const [loading, setLoading] = useState<boolean>(false);

    const refreshCsrfToken = () => {
        setLoading(true);

        const request = fetchCsrfCookie().then(token => {
            setCsrfToken(token);
            setLoading(false);
            return token;
        });

        // Kept for API compatibility with callers that await `.request`. The
        // underlying fetch is shared, so it is deliberately never aborted — one
        // component unmounting must not cancel it for everyone else.
        return { request, abortController: new AbortController() };
    };

    useEffect(() => {
        // If a token is already present the cookie is still good; re-fetching
        // only adds churn.
        if (getCookie('XSRF-TOKEN')) {
            setCsrfToken(getCookie('XSRF-TOKEN'));
            return;
        }

        void refreshCsrfToken();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        csrfToken,
        loading,
        refreshCsrfToken,
    };
};

export { useCsrf };
